/**
 * NotificationCenter — Dropdown panel for notifications, shown from the TopBar.
 * Features: unread badge count, list of notifications, mark all as read, mark single as read, delete.
 * Uses a polling query (60s) to keep notifications fresh.
 *
 * @example
 * <NotificationCenter open={isOpen} onClose={() => setOpen(false)} />
 */

import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Bell, CheckCheck, BellOff } from 'lucide-react';
import {
  useNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
} from '@/features/notifications/hooks/useNotifications';
import { NotificationItem } from '@/features/notifications/NotificationItem';
import { Spinner } from '@/components/ui/Spinner';
import type { AppNotification } from '@/lib/api/types';

export interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
  /** Ref to the bell button (for outside-click detection) */
  triggerRef?: React.RefObject<HTMLElement | null>;
}

export function NotificationCenter({ open, onClose, triggerRef }: NotificationCenterProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);

  const notifQ = useNotificationsQuery({ page, limit: 20 });
  const markReadMut = useMarkAsReadMutation();
  const markAllReadMut = useMarkAllAsReadMutation();
  const deleteMut = useDeleteNotificationMutation();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        panelRef.current &&
        !panelRef.current.contains(target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(target)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose, triggerRef]);

  // Close on escape
  useEffect(() => {
    if (!open) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const notifications = notifQ.data?.data || [];
  const total = notifQ.data?.total || 0;
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <>
      {/* Overlay (transparent, just for mobile) */}
      <div
        className="fixed inset-0 z-40 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={clsx(
          'absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-md bg-white rounded-xl shadow-lg border border-gray-200 z-50 flex flex-col',
          'max-h-[70vh] overflow-hidden',
        )}
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">
                {unreadCount} new
              </span>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllReadMut.mutate()}
              disabled={markAllReadMut.isPending}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-50"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {notifQ.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="md" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <BellOff className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No notifications yet</p>
              <p className="text-xs text-gray-300 mt-1">You'll see goal reminders, study updates, and more here.</p>
            </div>
          ) : (
            <>
              {notifications.map((notif: AppNotification) => (
                <NotificationItem
                  key={notif.id}
                  notification={notif}
                  onMarkRead={(id) => markReadMut.mutate(id)}
                  onDelete={(id) => deleteMut.mutate(id)}
                />
              ))}

              {/* Load more */}
              {notifications.length < total && (
                <div className="text-center py-3">
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Load more ({total - notifications.length} remaining)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default NotificationCenter;